import {
  IN_STOCK,
  OUT_OF_STOCK,
} from '@jouzen/ecom-components/v3/consts/stockStatus';
import { RingFinishEnum } from '@jouzen/ecom-components/v3/enums/ringFinish';
import type { RingFinish } from '@jouzen/ecom-components/v3/types/ringFinish';
import type { StockStatus } from '@jouzen/ecom-components/v3/types/stockStatus';
import type {
  EvaluationDetails,
  ExperimentEvaluation,
  TypedGet,
} from '@statsig/react-bindings';
import { useDynamicConfig } from '@statsig/react-bindings';

import { styleDepletionFallback } from '@/app/[locale]/product/_consts/inventoryInsights';
import type { StyleDepletionFallback } from '@/app/[locale]/product/_types/inventoryTypes';
import { SIZING_KIT } from '@/app/[locale]/store/_consts';
import { RING_FINISHES, RING_STYLES } from '@/app/consts/ring';
import { RingStyleEnum } from '@/app/enums/RingStyleEnum';
import type { RingStyle } from '@/app/types/ring';

export const DEFAULT_PRODUCTS_INVENTORY: UseProductsInventory = {
  getFinishStockStatus: () => IN_STOCK as StockStatus,
  getFirstStockedFinish: () => RingFinishEnum.Silver,
  getFirstStockedStyle: () => RingStyleEnum.OuraRing4,
  getSizeStockStatus: () => IN_STOCK as StockStatus,
  getStyleStockStatus: () => IN_STOCK as StockStatus,
  isSizesFullyDepletedForFinish: () => false,
  isSizesHalfDepletedForFinish: () => false,
};

export interface GetStyleStockStatus {
  style: RingStyle;
}

interface GetFinishStockStatus extends GetStyleStockStatus {
  finish: RingFinish;
}

interface GetSizeStockStatus extends GetFinishStockStatus {
  selectedSize: string;
}

export interface UseProductsInventory {
  getFirstStockedFinish: (props: GetStyleStockStatus) => RingFinish;
  getFirstStockedStyle: () => RingStyle;
  getFinishStockStatus: (props: GetFinishStockStatus) => StockStatus;
  getSizeStockStatus: (props: GetSizeStockStatus) => StockStatus;
  getStyleStockStatus: (props: GetStyleStockStatus) => StockStatus;
  isSizesFullyDepletedForFinish: (props: GetFinishStockStatus) => boolean;
  isSizesHalfDepletedForFinish: (props: GetFinishStockStatus) => boolean;
}

interface Config {
  readonly get: TypedGet;
  readonly name: string;
  readonly ruleID: string;
  readonly details: EvaluationDetails;
  readonly value: Record<string, unknown>;
  readonly __evaluation: ExperimentEvaluation | null;
}

interface GetFinishStock extends GetFinishStockStatus {
  gen3DepletionConfig: Config;
}

const _getFinishStock = ({
  finish,
  gen3DepletionConfig,
  style,
}: GetFinishStock) => {
  const config = gen3DepletionConfig;
  const styleInv = config.get(
    style,
    styleDepletionFallback,
  ) as StyleDepletionFallback;
  return styleInv[finish];
};

const useProductsInventory = (): UseProductsInventory => {
  const gen3DepletionConfig = useDynamicConfig(
    'inventory_insights_gen3_depletion',
  );

  // inventory status for style
  const getStyleStockStatus = ({ style }: GetStyleStockStatus) => {
    if (!RING_STYLES.includes(style)) return OUT_OF_STOCK;
    const config = gen3DepletionConfig;

    return (config.get(style, styleDepletionFallback) as StyleDepletionFallback)
      .stockStatus;
  };

  // inventory status for finish
  const getFinishStockStatus = ({ style, finish }: GetFinishStockStatus) => {
    if (!RING_STYLES.includes(style) || !RING_FINISHES.includes(finish))
      return OUT_OF_STOCK;
    const finishInv = _getFinishStock({
      finish,
      gen3DepletionConfig,
      style,
    });

    // DO NOT REMOVE. Need to fix as follow up 8/15/2025
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return finishInv?.stockStatus ?? OUT_OF_STOCK;
  };

  // is inventory at least half depleted in sizes for a finish
  const isSizesHalfDepletedForFinish = ({
    finish,
    style,
  }: GetFinishStockStatus): boolean => {
    const finishInv = _getFinishStock({
      finish,
      gen3DepletionConfig,
      style,
    });
    const sizes = finishInv.sizes;
    const totalSizes = sizes.length;
    const outOfStockCount = sizes.filter(
      (ring) => ring.stockStatus === OUT_OF_STOCK,
    ).length;
    return totalSizes / 2 <= outOfStockCount;
  };

  const isSizesFullyDepletedForFinish = ({
    finish,
    style,
  }: GetFinishStockStatus): boolean => {
    const finishInv = _getFinishStock({
      finish,
      gen3DepletionConfig,
      style,
    });
    const sizes = finishInv.sizes;
    const totalSizes = sizes.length;
    const outOfStockCount = sizes.filter(
      (ring) => ring.stockStatus === OUT_OF_STOCK,
    ).length;
    return totalSizes === outOfStockCount;
  };

  // inventory status for size
  const getSizeStockStatus = ({
    style,
    finish,
    selectedSize,
  }: GetSizeStockStatus) => {
    if (!RING_STYLES.includes(style) || !RING_FINISHES.includes(finish))
      return IN_STOCK;
    const config = gen3DepletionConfig;
    const finishInv = (
      config.get(style, styleDepletionFallback) as StyleDepletionFallback
    )[finish];

    const sizes = finishInv.sizes;
    if (selectedSize === SIZING_KIT) {
      return IN_STOCK;
    }
    return (
      sizes.find((ring) => ring.size == Number(selectedSize))?.stockStatus ??
      IN_STOCK
    );
  };

  // get the first stocked style
  const getFirstStockedStyle = () =>
    RING_STYLES.find(
      (style) => getStyleStockStatus({ style }) !== OUT_OF_STOCK,
    ) ?? RingStyleEnum.OuraRing4;

  // get the first stocked finish
  const getFirstStockedFinish = ({ style }: { style: RingStyle }) => {
    const inStockFinish = RING_FINISHES.find((finish) => {
      const stockedFinish = getFinishStockStatus({
        finish,
        style,
      });
      return stockedFinish !== OUT_OF_STOCK;
    });

    return inStockFinish ?? RingFinishEnum.Silver;
  };

  return {
    getFinishStockStatus,
    getFirstStockedFinish,
    getFirstStockedStyle,
    getSizeStockStatus,
    getStyleStockStatus,
    isSizesFullyDepletedForFinish,
    isSizesHalfDepletedForFinish,
  };
};

export default useProductsInventory;
